import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Rocket, Shield, Users, TrendingUp, CheckCircle, Star } from 'lucide-react';
import Spline from '@splinetool/react-spline';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { stats, featuredProjects, testimonials, faqs, mlmStructure } from '../data/mock';

const Home = () => {
  const navigate = useNavigate();
  const [liveProjects, setLiveProjects] = useState([]);

  useEffect(() => {
    setLiveProjects(featuredProjects.filter((p) => p.status === 'live').slice(0, 3));
  }, []);

  return (
    <div className="dark-container">
      {/* Hero Section with Spline */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center w-full">
          {/* Left Content */}
          <div className="z-10">
            <div className="inline-block px-4 py-2 bg-brand-primary/10 border border-brand-primary rounded-sm mb-6">
              <span className="badge-text text-sm font-medium">Trusted by 128K+ Investors</span>
            </div>
            <h1 className="display-huge text-white mb-6 leading-tight">
              Launch. Invest. Earn.
            </h1>
            <p className="body-large text-white/85 mb-8 leading-relaxed max-w-xl">
              Join the premier decentralized launchpad connecting crypto investors with vetted blockchain projects. Build wealth through early-stage investments and our multi-level referral system.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-12">
              <Button className="btn-primary" onClick={() => navigate('/projects')}>
                Explore Projects
                <ArrowRight size={18} />
              </Button>
              <Button className="btn-secondary" onClick={() => navigate('/referral')}>
                Join Network
              </Button>
            </div>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex items-center gap-2">
                <Shield className="text-brand-primary" size={20} />
                <span className="text-white/70 text-sm">Smart Contract Audited</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-brand-primary" size={20} />
                <span className="text-white/70 text-sm">KYC Verified</span>
              </div>
            </div>
          </div>

          {/* Right - Spline 3D */}
          <div className="relative h-[600px] md:h-[700px] w-full overflow-visible">
            <Spline scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 border-y border-white/25">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="display-large text-brand-primary mb-2">${stats.totalRaised}</div>
              <div className="text-white/60">Total Funds Raised</div>
            </div>
            <div className="text-center">
              <div className="display-large text-brand-primary mb-2">{stats.activeInvestors}</div>
              <div className="text-white/60">Active Investors</div>
            </div>
            <div className="text-center">
              <div className="display-large text-brand-primary mb-2">{stats.projectsLaunched}</div>
              <div className="text-white/60">Projects Launched</div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-4">Live Token Launches</h2>
            <p className="body-medium text-white/70">Invest in carefully vetted projects with massive growth potential</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {liveProjects.map((project) =>
            <Card key={project.id} className="bg-white/5 border border-white/25 backdrop-blur-xl hover:border-brand-primary transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <img src={project.logo} alt={project.name} className="w-14 h-14 rounded-lg object-cover" />
                    <span className="px-3 py-1 bg-brand-primary text-black text-xs font-medium rounded-sm">
                      {project.network}
                    </span>
                  </div>
                  
                  <h3 className="heading-2 text-white mb-2">{project.name}</h3>
                  <p className="text-white/60 text-sm mb-4">{project.description}</p>
                  
                  <div className="space-y-3 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">Progress</span>
                      <span className="text-brand-primary font-medium">{project.progress.toFixed(1)}%</span>
                    </div>
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div
                      className="h-full bg-brand-primary transition-all duration-500"
                      style={{ width: `${project.progress}%` }} />

                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/60">${project.raised}</span>
                      <span className="text-white/60">${project.hardCap}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-white/25">
                    <div>
                      <div className="text-white/60 text-xs mb-1">Token Price</div>
                      <div className="text-white font-medium">${project.tokenPrice}</div>
                    </div>
                    <Button
                    className="btn-primary px-6"
                    onClick={() => navigate('/projects')}>

                      Invest Now
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="text-center mt-12">
            <Button className="btn-secondary" onClick={() => navigate('/projects')}>
              View All Projects
              <ArrowRight size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-4">How It Works</h2>
            <p className="body-medium text-white/70">Start investing in 3 simple steps</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center group-hover:bg-brand-primary transition-all duration-300">
                <Rocket className="text-brand-primary group-hover:text-black transition-colors" size={32} />
              </div>
              <h3 className="heading-2 text-white mb-3">1. Connect Wallet</h3>
              <p className="text-white/60">Link your Web3 wallet (MetaMask, Trust Wallet) and complete KYC verification for secure access.</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center group-hover:bg-brand-primary transition-all duration-300">
                <TrendingUp className="text-brand-primary group-hover:text-black transition-colors" size={32} />
              </div>
              <h3 className="heading-2 text-white mb-3">2. Browse Projects</h3>
              <p className="text-white/60">Explore vetted blockchain projects with detailed tokenomics, roadmaps, and team information.</p>
            </div>

            <div className="text-center group">
              <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary/10 border border-brand-primary rounded-sm flex items-center justify-center group-hover:bg-brand-primary transition-all duration-300">
                <Users className="text-brand-primary group-hover:text-black transition-colors" size={32} />
              </div>
              <h3 className="heading-2 text-white mb-3">3. Invest & Earn</h3>
              <p className="text-white/60">Invest in promising projects and earn through token appreciation plus our MLM referral rewards.</p>
            </div>
          </div>
        </div>
      </section>

      {/* MLM / Referral Program Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-white/5 border border-brand-primary rounded-sm p-12 backdrop-blur-xl">
            <div className="text-center mb-12">
              <div className="inline-block px-4 py-2 bg-brand-primary/10 border border-brand-primary rounded-sm mb-6">
                <span className="text-brand-primary text-sm font-medium">Build Passive Income</span>
              </div>
              <h2 className="display-medium text-white mb-4">Multi-Level Referral Program</h2>
              <p className="body-large text-white/70 max-w-3xl mx-auto">
                Earn commissions up to 5 levels deep. Build your network once, earn forever from every investment your team makes.
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary rounded-sm flex items-center justify-center">
                  <TrendingUp className="text-black" size={36} />
                </div>
                <h3 className="heading-2 text-white mb-3">Passive Income</h3>
                <p className="text-white/70 leading-relaxed">
                  Build once, earn forever. Your network continues generating income 24/7, even while you sleep.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary rounded-sm flex items-center justify-center">
                  <Users className="text-black" size={36} />
                </div>
                <h3 className="heading-2 text-white mb-3">Network Growth</h3>
                <p className="text-white/70 leading-relaxed">
                  Earn from 5 levels of referrals. Your team's success directly translates to your earnings.
                </p>
              </div>

              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-brand-primary rounded-sm flex items-center justify-center">
                  <Shield className="text-black" size={36} />
                </div>
                <h3 className="heading-2 text-white mb-3">Unlimited Potential</h3>
                <p className="text-white/70 leading-relaxed">
                  No earning caps. Scale your income infinitely as you grow your network of investors.
                </p>
              </div>
            </div>

            {/* Example Earnings */}
            <div className="bg-black/50 border border-white/25 rounded-sm p-8 mb-12">
              <h3 className="heading-2 text-white text-center mb-6">Example Monthly Earnings</h3>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-white/60 text-sm mb-2">10 Direct Referrals</div>
                  <div className="text-brand-primary text-3xl font-bold mb-1">$2,500</div>
                  <div className="text-white/60 text-xs">Average monthly passive income</div>
                </div>
                <div className="text-center">
                  <div className="text-white/60 text-sm mb-2">50 Network Members</div>
                  <div className="text-brand-primary text-3xl font-bold mb-1">$8,750</div>
                  <div className="text-white/60 text-xs">Growing network rewards</div>
                </div>
                <div className="text-center">
                  <div className="text-white/60 text-sm mb-2">200+ Network Members</div>
                  <div className="text-brand-primary text-3xl font-bold mb-1">$25,000+</div>
                  <div className="text-white/60 text-xs">Top earner potential</div>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="mb-12">
              <h3 className="heading-1 text-white text-center mb-8">How Referral Program Works</h3>
              <div className="grid md:grid-cols-4 gap-6">
                <div className="bg-black/50 border border-white/25 rounded-sm p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-brand-primary text-black rounded-sm flex items-center justify-center text-xl font-bold">
                    1
                  </div>
                  <h4 className="text-white font-semibold mb-2">Get Your Link</h4>
                  <p className="text-white/60 text-sm">Receive your unique referral code upon registration</p>
                </div>
                <div className="bg-black/50 border border-white/25 rounded-sm p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-brand-primary text-black rounded-sm flex items-center justify-center text-xl font-bold">
                    2
                  </div>
                  <h4 className="text-white font-semibold mb-2">Share & Invite</h4>
                  <p className="text-white/60 text-sm">Share with friends, family, and your network</p>
                </div>
                <div className="bg-black/50 border border-white/25 rounded-sm p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-brand-primary text-black rounded-sm flex items-center justify-center text-xl font-bold">
                    3
                  </div>
                  <h4 className="text-white font-semibold mb-2">They Invest</h4>
                  <p className="text-white/60 text-sm">Your referrals invest in token launches</p>
                </div>
                <div className="bg-black/50 border border-white/25 rounded-sm p-6 text-center">
                  <div className="w-12 h-12 mx-auto mb-4 bg-brand-primary text-black rounded-sm flex items-center justify-center text-xl font-bold">
                    4
                  </div>
                  <h4 className="text-white font-semibold mb-2">You Earn</h4>
                  <p className="text-white/60 text-sm">Instant commissions from entire network</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <h3 className="heading-1 text-white mb-4">Ready to Build Your Network?</h3>
              <p className="text-white/70 mb-8 max-w-2xl mx-auto">
                Join thousands earning passive income through our proven referral system. Start building your empire today.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button className="btn-primary text-lg px-8" onClick={() => navigate('/signup')}>
                  Start Building Your Network
                  <ArrowRight size={20} />
                </Button>
                <Button className="btn-secondary text-lg px-8" onClick={() => navigate('/referral')}>
                  Learn More About Referrals
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-4">Trusted by Investors Worldwide</h2>
            <p className="body-medium text-white/70">Real results from our community</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) =>
            <Card key={testimonial.id} className="bg-white/5 border border-white/25 backdrop-blur-xl">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) =>
                  <Star key={i} className="text-brand-primary fill-brand-primary" size={16} />
                  )}
                  </div>
                  <p className="text-white/85 mb-6 leading-relaxed">{testimonial.text}</p>
                  <div className="flex items-center gap-3">
                    <img src={testimonial.image} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" />
                    <div>
                      <div className="text-white font-medium">{testimonial.name}</div>
                      <div className="text-white/60 text-sm">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="display-medium text-white mb-4">Frequently Asked Questions</h2>
            <p className="body-medium text-white/70">Everything you need to know about MatrixLaunch</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) =>
            <AccordionItem
              key={faq.id}
              value={`item-${faq.id}`}
              className="bg-white/5 border border-white/25 backdrop-blur-xl rounded-sm px-6">

                <AccordionTrigger className="text-white hover:text-brand-primary text-left py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-white/70 pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="bg-white/5 border border-brand-primary rounded-sm p-12 backdrop-blur-xl">
            <h2 className="display-medium text-white mb-4">Ready to Start Your Investment Journey?</h2>
            <p className="body-large text-white/70 mb-8">Join thousands of investors and start building wealth today</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="btn-primary" onClick={() => navigate('/projects')}>
                Explore Projects
                <ArrowRight size={18} />
              </Button>
              <Button className="btn-secondary" onClick={() => navigate('/referral')}>
                Learn About Referrals
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>);

};

export default Home;